// IntelViewTabs — segmented control switching between MAP / TIMELINE / ANALYTICS.
//
// One filter state, three views, zero re-fetch on switch (per the design plan).
// Tabs themselves are presentational; the parent passes the correct body for
// each view as `children`. Keyboard shortcuts: `g m / g t / g a` (only when no
// input is focused).

import type { intel } from '@eidollon/contracts';
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { tinykeys } from 'tinykeys';

interface Tab {
  id: intel.IntelView;
  label: string;
}

const TABS: Tab[] = [
  { id: 'map', label: 'MAP' },
  { id: 'timeline', label: 'TIMELINE' },
  // Analytics view disabled for now (founder request 2026-06-04). Re-enable by
  // restoring this tab + the 'g a' shortcut below.
  // { id: 'analytics', label: 'ANALYTICS' },
];

export interface IntelViewTabsProps {
  view: intel.IntelView;
  onViewChange: (next: intel.IntelView) => void;
  children: ReactNode;
  /** Optional right-side controls (e.g. Timeline group-by chip). Sits to the
   *  left of the keyboard hint. */
  rightSlot?: ReactNode;
}

export function IntelViewTabs({ view, onViewChange, children, rightSlot }: IntelViewTabsProps) {
  // Latest-onChange ref so the tinykeys handler always sees the live setter
  // without re-binding listeners on every render.
  const onChangeRef = useRef(onViewChange);
  useEffect(() => {
    onChangeRef.current = onViewChange;
  }, [onViewChange]);

  useEffect(() => {
    const guard = (cb: () => void) => (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/i.test(target.tagName)) return;
      if (target?.isContentEditable) return;
      e.preventDefault();
      cb();
    };
    return tinykeys(window, {
      'g m': guard(() => onChangeRef.current('map')),
      'g t': guard(() => onChangeRef.current('timeline')),
      // 'g a' (analytics) disabled — see TABS above.
    });
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div
        role="tablist"
        aria-label="intel view"
        className="pane-h"
        style={{ display: 'flex', gap: 0, alignItems: 'stretch', padding: 0 }}
      >
        {TABS.map((t) => {
          const on = t.id === view;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={on}
              aria-controls={`intel-view-${t.id}`}
              onClick={() => onViewChange(t.id)}
              style={{
                height: 28,
                padding: '0 12px',
                background: on ? 'var(--bg-pane-2)' : 'transparent',
                border: 'none',
                borderRight: '1px solid var(--bd)',
                borderBottom: on ? '1px solid var(--primary)' : '1px solid transparent',
                color: on ? 'var(--fg-1)' : 'var(--fg-3)',
                fontFamily: 'var(--mono)',
                fontSize: 11,
                letterSpacing: 0.4,
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          );
        })}
        <span
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            paddingRight: 10,
          }}
        >
          {rightSlot}
          <span
            style={{
              color: 'var(--fg-3)',
              fontFamily: 'var(--mono)',
              fontSize: 10,
            }}
            title="press g then m / t / a"
          >
            g · m / t / a
          </span>
        </span>
      </div>
      <div
        id={`intel-view-${view}`}
        role="tabpanel"
        style={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </div>
    </div>
  );
}
